import { useCallback, useMemo, useState } from 'react';
import type { DocumentDeviceAssignmentState } from '../components/fileDetailPanel/DocumentDeviceAssignmentSection';
import type { DeviceDocumentAccess } from '../types';

// Manages device assignment editable state, validation status, and change tracking.
export function useDocumentDeviceState(
  initialState: DocumentDeviceAssignmentState | undefined
) {
  const [deviceState, setDeviceState] = useState<DocumentDeviceAssignmentState | undefined>(
    initialState
  );
  const [savedDeviceState, setSavedDeviceState] = useState<
    DocumentDeviceAssignmentState | undefined
  >(initialState);
  const [hasError, setHasError] = useState(false);

  // Updates current device assignment together with the latest validation status.
  const update = useCallback(
    (value: DocumentDeviceAssignmentState | undefined, hasErrors: boolean) => {
      setDeviceState(value);
      setHasError(hasErrors);
    },
    []
  );

  // Replaces current and saved device baseline from API access data.
  const resetWith = useCallback((access: DeviceDocumentAccess | null) => {
    const nextDeviceState = mapAccessToAssignmentState(access);
    setDeviceState(nextDeviceState);
    setSavedDeviceState(nextDeviceState);
    setHasError(false);
  }, []);

  // Exposes derived value flags used by save and validation controls.
  const deviceValueState = useMemo(
    () => ({
      hasError,
      hasChanges: hasDeviceChanges(deviceState, savedDeviceState),
    }),
    [hasError, deviceState, savedDeviceState]
  );

  return {
    deviceState,
    deviceValueState,
    update,
    resetWith,
  };
}

// Converts API device access shape to the local assignment form shape.
function mapAccessToAssignmentState(
  access: DeviceDocumentAccess | null
): DocumentDeviceAssignmentState | undefined {
  if (!access) {
    return undefined;
  }

  return {
    deviceModelId: access.deviceModelId,
    userLevel: access.userLevel,
  };
}

// Compares current and saved device assignment snapshots for meaningful changes.
function hasDeviceChanges(
  currentState: DocumentDeviceAssignmentState | undefined,
  savedState: DocumentDeviceAssignmentState | undefined
): boolean {
  if (!currentState && !savedState) {
    return false;
  }

  if (!currentState || !savedState) {
    return true;
  }

  return (
    currentState.deviceModelId !== savedState.deviceModelId ||
    currentState.userLevel !== savedState.userLevel
  );
}
